import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { detectPlatform } from "../../platform.js";
import {
  useCalendarFocusStore,
  useCalendarsStore,
  useSettingsStore,
  useTaskDialogStore,
  useTasksStore,
} from "../../stores/index.js";
import { notificationCalendarTarget } from "../reminders/notification-calendar-target.js";

const SCHEME = "yourcalendar:";

/**
 * Android only: routes `yourcalendar://` deep links fired by the home-screen
 * widgets. Handled both warm (appUrlOpen) and cold (getLaunchUrl — a widget tap
 * can start the whole app). Targets:
 *
 * - `yourcalendar://task?id=<taskOrVirtualId>` — calendar jump + flash
 *   highlight, same path as a notification body tap.
 * - `yourcalendar://today` / `yourcalendar://priorities` — open that view.
 * - `yourcalendar://calendar?view=month|week|days|day` — open that calendar
 *   sub-view (the calendar-view widgets' tap target).
 * - `yourcalendar://create` — open the create sheet.
 *
 * The Google OAuth redirect uses its own reversed-client-id scheme and its
 * listener filters on that prefix, so the two never collide. Mount once, in
 * Shell (inside the router).
 */
export function useWidgetDeepLinks(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (detectPlatform() !== "capacitor") return;
    let removed = false;
    let requestId = 0;
    let liveUrlReceived = false;

    const handle = (rawUrl: string) => {
      if (!rawUrl.toLowerCase().startsWith(SCHEME)) return;
      let url: URL;
      try {
        url = new URL(rawUrl);
      } catch {
        return;
      }
      const target = url.host || url.pathname.replace(/^\/+/, "");
      if (target === "today") {
        requestId++;
        return void navigate("/today");
      }
      if (target === "priorities") {
        requestId++;
        return void navigate("/priorities");
      }
      if (target === "calendar") {
        requestId++;
        const view = url.searchParams.get("view") ?? "";
        return void navigate(`/calendar/${["month", "week", "days", "day"].includes(view) ? view : "month"}`);
      }
      if (target === "create") {
        const request = ++requestId;
        void (async () => {
          // The form snapshots its defaults when it opens. A cold-start tap can
          // arrive before Shell's hydration effects, so wait for the settings
          // and default-calendar bootstrap instead of seeding stale defaults.
          await useSettingsStore.getState().hydrate();
          await useCalendarsStore.getState().hydrate();
          if (!removed && request === requestId) useTaskDialogStore.getState().openCreate();
        })().catch(() => { /* leave the app open when startup storage is unavailable */ });
        return;
      }
      if (target !== "task") return;
      const id = url.searchParams.get("id");
      if (!id) return;
      const request = ++requestId;
      void (async () => {
        // A cold-start tap can beat store hydration; both hydrates are deduped.
        await Promise.all([useTasksStore.getState().hydrate(), useSettingsStore.getState().hydrate()]);
        if (removed || request !== requestId) return;
        const store = useTasksStore.getState();
        const calTarget = notificationCalendarTarget(id, store.tasks, store.overrides);
        if (!calTarget) return;
        useCalendarFocusStore.getState().focus(new Date(calTarget.dueIso), calTarget.highlightId);
        navigate(`/calendar/${useSettingsStore.getState().lastCalendarView}`);
      })().catch(() => { /* leave the app open when startup storage is unavailable */ });
    };

    let remove: (() => void) | null = null;
    void (async () => {
      const { App } = await import("@capacitor/app");
      // Register first so a warm widget tap cannot fall into the narrow gap
      // between starting getLaunchUrl() and the native listener becoming live.
      try {
        const listener = await App.addListener("appUrlOpen", (event: { url: string }) => {
          // OAuth and other app links share this native event. Only a newer
          // widget URL should suppress the older cold-launch widget URL.
          if (!event.url.toLowerCase().startsWith(SCHEME)) return;
          liveUrlReceived = true;
          handle(event.url);
        });
        if (removed) await listener.remove();
        else remove = () => void listener.remove();
      } catch { /* cold-launch lookup below still works without the listener */ }

      if (removed) return;
      try {
        // Cold start: the launch intent's data URL is not replayed via appUrlOpen.
        const launch = await App.getLaunchUrl();
        // A warm URL received after registration represents the newer action.
        if (!removed && !liveUrlReceived && launch?.url) handle(launch.url);
      } catch { /* no launch URL available */ }
    })().catch(() => { /* optional native app bridge was unavailable */ });

    return () => {
      removed = true;
      requestId++;
      remove?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
